import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ArrowLeft, Download, Loader2, AlertCircle } from 'lucide-react';
import { jobsAPI } from '../services/api';
import type { TaxonomyEntry, ValidationQueueEntry } from '../types';
import { ValidationProgressBar } from '../components/validation/ValidationProgressBar';
import { ValidationFilterBar, type FilterState } from '../components/validation/ValidationFilterBar';
import { BulkActionBar, type TaxonomyRefreshResult } from '../components/validation/BulkActionBar';
import { extractApiError } from '../utils/errorMessages';
import { useToast } from '../hooks/use-toast';
import { ValidationGrid } from '../components/validation/ValidationGrid';

const JobValidation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Filter / search / pagination — stored in the URL so they survive navigation and page refresh
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFilter = (searchParams.get('filter') ?? 'all') as FilterState;
  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10));
  const searchValue = searchParams.get('search') ?? '';
  const rawPageSize = parseInt(searchParams.get('page_size') ?? '25', 10);
  const pageSize = [10, 25, 50, 100].includes(rawPageSize) ? rawPageSize : 25;
  const sort = searchParams.get('sort') ?? '';

  // ── sessionStorage persistence ────────────────────────────────────────────
  // On first mount with no URL params, restore the last-used filter/page/search
  // from sessionStorage. On every subsequent render, save the current state.
  //
  // Both operations are merged into ONE effect to prevent the race condition
  // where a separate "save" effect fires on the initial render with default
  // values (all / page 1) and overwrites stored state before the restore's
  // setSearchParams has triggered a re-render with the correct values.
  const didInitRef = useRef(false);
  useEffect(() => {
    if (!id) return;

    if (!didInitRef.current) {
      didInitRef.current = true;
      const hasUrlParams = ['filter', 'page', 'search', 'page_size', 'sort'].some(
        (k) => searchParams.get(k),
      );
      if (!hasUrlParams) {
        try {
          const raw = sessionStorage.getItem(`vq-${id}`);
          const stored = raw ? (JSON.parse(raw) as Record<string, string>) : null;
          if (stored && Object.keys(stored).length > 0) {
            setSearchParams(stored, { replace: true });
            // Return WITHOUT saving — the re-render triggered by setSearchParams
            // will run this effect again with the restored state and save then.
            return;
          }
        } catch { /* ignore corrupt storage */ }
      }
    }

    // Save current params to sessionStorage
    const params: Record<string, string> = {};
    if (activeFilter !== 'all') params.filter = activeFilter;
    if (page > 1) params.page = String(page);
    if (searchValue) params.search = searchValue;
    if (pageSize !== 25) params.page_size = String(pageSize);
    if (sort) params.sort = sort;
    sessionStorage.setItem(`vq-${id}`, JSON.stringify(params));
  }, [id, activeFilter, page, pageSize, sort, searchValue, searchParams, setSearchParams]);
  // ─────────────────────────────────────────────────────────────────────────

  // Local edit map: part_id → selected_category_id
  // null = unreviewed / cleared; '' = explicitly excluded; Relay ID = edited
  const [editMap, setEditMap] = useState<Map<string, string | null>>(new Map());

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [downloadingExport, setDownloadingExport] = useState(false);

  // Draft-save state (persists edits to backend so server-side filter stays in sync)
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Taxonomy refresh state
  const [refreshing, setRefreshing] = useState(false);
  const [taxonomyRefreshResult, setTaxonomyRefreshResult] = useState<TaxonomyRefreshResult | null>(null);

  // Fetch validation queue page (server-side pagination + filter)
  const queryParams = useMemo(
    () => ({
      page,
      page_size: pageSize,
      ...(activeFilter !== 'all' ? { state: activeFilter } : {}),
      ...(searchValue.trim() ? { search: searchValue.trim() } : {}),
      ...(sort ? { sort } : {}),
    }),
    [page, pageSize, activeFilter, searchValue, sort],
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['validation-queue', id, queryParams],
    queryFn: () => jobsAPI.validationQueue(id!, queryParams),
    enabled: !!id,
    staleTime: 30_000,
  });

  const queueData = data?.data;
  const entries: ValidationQueueEntry[] = queueData?.results ?? [];
  const taxonomy: TaxonomyEntry[] = queueData?.taxonomy_snapshot ?? [];
  const total: number = queueData?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  // ── Page-clamp guard ──────────────────────────────────────────────────
  // Silently redirects to the last valid page when `page` exceeds `totalPages`.
  // Handles stale sessionStorage (e.g. page=8 stored when pageSize was 10, then
  // pageSize changed to 100 leaving only 3 pages) and direct URL pastes.
  // `page` is in the dep array so the effect also fires on manual URL edits that
  // change only the page number without affecting total or pageSize.
  useEffect(() => {
    if (total > 0) {
      const maxPage = Math.ceil(total / pageSize);
      if (page > maxPage) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('page', String(maxPage));
            return next;
          },
          { replace: true },
        );
      }
    }
  }, [total, pageSize, page, setSearchParams]);
  // ───────────────────────────────────────────────────────────────────────────

  // Derive live counts by merging editMap over server counters
  const derivedCounts = useMemo(() => {
    // Start from server-reported counts
    let unreviewed = queueData?.unreviewed_count ?? 0;
    let edited = queueData?.edited_count ?? 0;
    let excluded = queueData?.excluded_count ?? 0;

    // Adjust based on local pending edits
    editMap.forEach((value, partId) => {
      // Find the stored entry from server (may not be on current page — use lightweight heuristic)
      const entry = entries.find((e) => e.part_id === partId);
      if (!entry) return; // Not on this page — skip delta (server counts already reflect stored state)

      const storedSelected = entry.selected_category_id;
      const wasUnreviewed = storedSelected === null;
      const wasEdited = storedSelected !== null && storedSelected !== '' && storedSelected !== entry.ai_category_1_id;
      const wasExcluded = storedSelected === '';

      const isNowUnreviewed = value === null;
      const isNowEdited = value !== null && value !== '' && value !== entry.ai_category_1_id;
      const isNowExcluded = value === '';

      if (wasUnreviewed && !isNowUnreviewed) unreviewed -= 1;
      if (!wasUnreviewed && isNowUnreviewed) unreviewed += 1;
      if (wasEdited && !isNowEdited) edited -= 1;
      if (!wasEdited && isNowEdited) edited += 1;
      if (wasExcluded && !isNowExcluded) excluded -= 1;
      if (!wasExcluded && isNowExcluded) excluded += 1;
    });

    return { unreviewed, edited, excluded, all: total };
  }, [editMap, entries, queueData, total]);

  /**
   * Flush any pending editMap entries to the backend via save-validation/.
   * Clears the editMap on success so the server state becomes authoritative
   * and server-side filters (edited / excluded / unreviewed) work correctly.
   */
  const flushEdits = useCallback(async (): Promise<void> => {
    if (!id || editMap.size === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const decisions = Array.from(editMap.entries()).map(
        ([part_id, selected_category_id]) => ({ part_id, selected_category_id }),
      );
      await jobsAPI.saveValidation(id, decisions);
      setEditMap(new Map());
    } catch (err: unknown) {
      setSaveError(
        extractApiError(
          err,
          'Could not save progress — your edits are still held locally and will be applied on final submit.',
        ),
      );
    } finally {
      setSaving(false);
    }
  }, [id, editMap]);

  const handleFilterChange = useCallback(
    async (filter: FilterState) => {
      await flushEdits();
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('filter', filter);
          next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    },
    [flushEdits, setSearchParams],
  );

  const handlePageSizeChange = useCallback(
    async (size: number) => {
      await flushEdits();
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('page_size', String(size));
          next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    },
    [flushEdits, setSearchParams],
  );

  const handleSortChange = useCallback(
    async (newSort: string) => {
      await flushEdits();
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (newSort) next.set('sort', newSort);
          else next.delete('sort');
          next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    },
    [flushEdits, setSearchParams],
  );

  const handleDecisionChange = useCallback(
    (partId: string, selectedCategoryId: string | null) => {
      setEditMap((prev) => {
        const next = new Map(prev);
        next.set(partId, selectedCategoryId);
        return next;
      });
    },
    [],
  );

  const handleRefreshTaxonomy = useCallback(async () => {
    if (!id) return;
    setRefreshing(true);
    setTaxonomyRefreshResult(null);
    try {
      const res = await jobsAPI.refreshTaxonomy(id);
      setTaxonomyRefreshResult(res.data);
      await queryClient.invalidateQueries({ queryKey: ['validation-queue', id] });
    } catch (err: unknown) {
      setSaveError(extractApiError(err, 'Failed to refresh taxonomy.'));
    } finally {
      setRefreshing(false);
    }
  }, [id, queryClient]);

  const handleApproveAll = useCallback(async () => {
    if (!id) return;
    // Merge any local edits + mark ALL remaining unreviewed entries as AI suggestion
    setSaving(true);
    setSaveError(null);
    try {
      const decisions = Array.from(editMap.entries()).map(
        ([part_id, selected_category_id]) => ({ part_id, selected_category_id }),
      );
      await jobsAPI.saveValidation(id, decisions, true /* approve_all_unreviewed */);
      setEditMap(new Map());
      // Refresh every cached page of the queue so counts and states are up to date
      await queryClient.invalidateQueries({ queryKey: ['validation-queue', id] });
    } catch (err: unknown) {
      setSaveError(
        extractApiError(err, 'Could not approve all — your local edits are still held and will be applied on final submit.'),
      );
    } finally {
      setSaving(false);
    }
  }, [id, editMap, queryClient]);

  const handleSubmit = async () => {
    if (!id) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      // Build full decision array: all entries from editMap + fill unreviewed with ai_category_1_id
      // The server expects ALL parts in one call (or we POST page-by-page — we do single POST here)
      // We need all entries, but we only have the current page. Fetch all if needed.
      // Strategy: POST the editMap decisions PLUS entries not in editMap use their ai_category_1_id.
      // For large queues: we send only the local edits; server fills unreviewed with ai_category_1_id.
      // (The approve-validation endpoint handles null decision as "use ai_category_1_id".)

      const decisions = Array.from(editMap.entries()).map(([part_id, selected_category_id]) => ({
        part_id,
        // '' = explicitly excluded; null = unreviewed; ID string = approved
        // Send the value as-is — the backend approve_validation endpoint handles all three.
        selected_category_id,
      }));

      await jobsAPI.approveValidation(id, decisions);
      navigate(`/jobs/${id}`);
    } catch (err: unknown) {
      setSubmitError(extractApiError(err, 'Submission failed. Please try again.'));
      setSubmitting(false);
    }
  };

  const handleDownloadDraftExport = async () => {
    if (!id) return;
    setDownloadingExport(true);
    try {
      const response = await jobsAPI.exportValidation(id, true);
      const url = window.URL.createObjectURL(
        new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = `part_category_draft_export_${String(id).slice(0, 8)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      toast({ title: "Couldn't download the draft export", variant: 'error' });
    } finally {
      setDownloadingExport(false);
    }
  };

  const filterCounts = {
    all: total,
    unreviewed: derivedCounts.unreviewed,
    edited: derivedCounts.edited,
    excluded: derivedCounts.excluded,
  };

  return (
    <div
      className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6"
      data-testid="validation-page"
    >
      {/* Header */}
      <div className="mb-4">
        <Link
          to={`/jobs/${id}`}
          className="inline-flex items-center text-sm text-text-tertiary hover:text-text-secondary transition-colors mb-2"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Job
        </Link>
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-text-primary">
              Part Category Validation
            </h2>
            <p className="text-sm text-text-tertiary mt-0.5">
              Review and approve AI category assignments before writing to digiPART
            </p>
          </div>
          <button
            type="button"
            onClick={handleDownloadDraftExport}
            disabled={downloadingExport}
            data-testid="download-draft-export-btn"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-border text-text-secondary hover:bg-bg-subtle transition-colors disabled:opacity-50"
          >
            {downloadingExport ? (
              <><Loader2 className="w-4 h-4 animate-spin" />Downloading...</>
            ) : (
              <><Download className="w-4 h-4" />Download Draft Export</>
            )}
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-bg-surface rounded-lg border border-border p-4 mb-4">
        {total > 0 ? (
          <ValidationProgressBar
            total={total}
            editedCount={derivedCounts.edited}
            excludedCount={derivedCounts.excluded}
            unreviewedCount={derivedCounts.unreviewed}
          />
        ) : (
          <div className="text-sm text-text-tertiary">Loading progress data...</div>
        )}
      </div>

      {/* Filter Bar + Bulk Actions */}
      <div className="bg-bg-surface rounded-lg border border-border p-3 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-1">
            {saving && (
              <span className="flex items-center gap-1 text-xs text-text-tertiary whitespace-nowrap">
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving…
              </span>
            )}
            <ValidationFilterBar
              activeFilter={activeFilter}
              searchValue={searchValue}
              onFilterChange={handleFilterChange}
              onSearchChange={(s) => {
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    if (s) next.set('search', s);
                    else next.delete('search');
                    next.set('page', '1');
                    return next;
                  },
                  { replace: true },
                );
              }}
              counts={filterCounts}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
          <BulkActionBar
            unreviewedCount={derivedCounts.unreviewed}
            total={total}
            onApproveAll={handleApproveAll}
            onSubmit={handleSubmit}
            submitting={submitting}
            onRefreshTaxonomy={handleRefreshTaxonomy}
            refreshing={refreshing}
            taxonomyRefreshResult={taxonomyRefreshResult}
          />
        </div>
      </div>

      {/* Save-progress error */}
      {saveError && (
        <div className="mb-4 p-3 bg-qc-warning/10 border border-qc-warning/30 rounded-lg flex items-start gap-2 text-sm text-qc-warning">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{saveError}</span>
        </div>
      )}

      {/* Submit error */}
      {submitError && (
        <div className="mb-4 p-3 bg-qc-error/10 border border-qc-error/30 rounded-lg flex items-start gap-2 text-sm text-qc-error">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{submitError}</span>
        </div>
      )}

      {/* Grid */}
      {isLoading && (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-8 h-8 animate-spin text-interactive-primary" />
          <span className="ml-3 text-text-tertiary">Loading validation queue...</span>
        </div>
      )}

      {isError && (
        <div className="p-6 text-center">
          <AlertCircle className="w-12 h-12 text-qc-error mx-auto mb-3" />
          <p className="text-text-secondary">
            {axios.isAxiosError(error)
              ? (error.response?.data?.detail ?? 'Failed to load validation queue.')
              : 'Failed to load validation queue.'}
          </p>
        </div>
      )}

      {!isLoading && !isError && (
        <>
          <ValidationGrid
            entries={entries}
            taxonomy={taxonomy}
            editMap={editMap}
            onDecisionChange={handleDecisionChange}
            pageOffset={(page - 1) * pageSize}
            sort={sort}
            onSortChange={handleSortChange}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-text-tertiary">
                Page {page} of {totalPages} ({total.toLocaleString()} total)
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setSearchParams(
                      (prev) => { const n = new URLSearchParams(prev); n.set('page', String(Math.max(1, page - 1))); return n; },
                      { replace: true },
                    )
                  }
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm rounded-lg border border-border text-text-secondary hover:bg-bg-subtle disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ← Previous
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setSearchParams(
                      (prev) => { const n = new URLSearchParams(prev); n.set('page', String(Math.min(totalPages, page + 1))); return n; },
                      { replace: true },
                    )
                  }
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 text-sm rounded-lg border border-border text-text-secondary hover:bg-bg-subtle disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default JobValidation;
